import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { AppLayout } from "../components/tabon/AppLayout";
import { toast } from "sonner";
import { Plus, Trash2, Gift, Stamp, Check, Power, X } from "lucide-react";

const TRIGGER_LABEL = {
  on_tab_open: "When a bottle tab is opened",
  on_first_visit: "On customer's first visit",
  manual: "Manual grant only",
};

export default function Rewards() {
  const [tab, setTab] = useState("stamps");
  return (
    <AppLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-5">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Rewards</h1>
          <p className="text-sm text-[#A0A0A0]">
            Digital stamp cards, trigger offers and manual rewards for your customers
          </p>
        </div>

        <div className="flex gap-2 mb-5 border-b border-white/5">
          {[
            { k: "stamps", label: "Stamp Programs", icon: Stamp },
            { k: "offers", label: "Trigger Offers", icon: Gift },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`rewards-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition relative ${
                tab === t.k ? "text-white" : "text-[#A0A0A0] hover:text-white"
              }`}
            >
              <t.icon size={16} /> {t.label}
              {tab === t.k && (
                <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#9D00FF]" />
              )}
            </button>
          ))}
        </div>

        {tab === "stamps" && <StampPrograms />}
        {tab === "offers" && <TriggerOffers />}
      </div>
    </AppLayout>
  );
}

function StampPrograms() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const { data } = await api.get("/stamp-programs");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (p) => {
    await api.put(`/stamp-programs/${p.id}`, { active: !p.active });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this program? Existing stamp cards will no longer earn.")) return;
    await api.delete(`/stamp-programs/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setEditing({})}
          data-testid="new-stamp-program"
          className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2"
        >
          <Plus size={16} /> New Stamp Program
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && (
          <div className="col-span-full bg-[#1A1A1A] border border-dashed border-white/10 rounded-xl p-10 text-center">
            <Stamp size={28} className="mx-auto text-[#9D00FF] mb-2" />
            <div className="font-semibold">No stamp programs yet</div>
            <p className="text-sm text-[#A0A0A0]">
              Create your first program — e.g. "Buy 6 cocktails, get 1 free".
            </p>
          </div>
        )}
        {rows.map((p) => (
          <div key={p.id} data-testid={`stamp-program-${p.id}`} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#9D00FF] font-bold">STAMP PROGRAM</div>
                <h3 className="text-lg font-bold">{p.name}</h3>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${
                p.active
                  ? "bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40"
                  : "bg-white/10 text-[#A0A0A0] border-white/20"
              }`}>
                {p.active ? "Active" : "Paused"}
              </span>
            </div>
            <div className="text-sm text-[#A0A0A0] mb-2">
              Buy <span className="text-white font-bold">{p.required_stamps}</span> {p.item_label}
              {p.required_stamps !== 1 ? "s" : ""} → <span className="text-white font-semibold">{p.reward_title}</span>
            </div>
            {p.reward_description && (
              <p className="text-xs text-[#A0A0A0] mb-2">{p.reward_description}</p>
            )}
            {p.reward_expires_days && (
              <p className="text-[11px] text-[#F59E0B]">
                Reward expires in {p.reward_expires_days} days once earned
              </p>
            )}
            <div className="flex gap-2 mt-auto pt-3">
              <button
                onClick={() => setEditing(p)}
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2 text-sm font-semibold hover:border-[#9D00FF]/50"
              >
                Edit
              </button>
              <button
                onClick={() => toggleActive(p)}
                data-testid={`toggle-program-${p.id}`}
                className="px-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm font-semibold hover:border-[#9D00FF]/50"
              >
                <Power size={14} />
              </button>
              <button
                onClick={() => del(p.id)}
                data-testid={`delete-program-${p.id}`}
                className="px-3 bg-[#0A0A0A] border border-[#EF4444]/30 rounded-lg text-sm font-semibold text-[#EF4444] hover:bg-[#EF4444]/10"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <StampProgramForm
          program={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StampProgramForm({ program, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: program?.name || "",
    item_label: program?.item_label || "",
    required_stamps: program?.required_stamps || 6,
    reward_title: program?.reward_title || "",
    reward_description: program?.reward_description || "",
    reward_expires_days: program?.reward_expires_days || "",
    active: program?.active !== false,
  });
  const [loading, setLoading] = useState(false);
  const editing = !!program?.id;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        required_stamps: Number(form.required_stamps),
        reward_expires_days: form.reward_expires_days ? Number(form.reward_expires_days) : null,
      };
      if (editing) {
        await api.put(`/stamp-programs/${program.id}`, payload);
      } else {
        await api.post("/stamp-programs", payload);
      }
      toast.success(editing ? "Program updated" : "Program created");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-lg p-6 tb-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{editing ? "Edit" : "New"} Stamp Program</h2>
          <button onClick={onClose} className="p-2 text-[#A0A0A0] hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Program Name">
            <input
              data-testid="stamp-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Cocktail Hour Rewards"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Item label">
              <input
                data-testid="stamp-item" required value={form.item_label}
                onChange={(e) => setForm({ ...form, item_label: e.target.value })}
                placeholder="cocktail · coffee · dessert"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
              />
            </Field>
            <Field label="Buy how many?">
              <input
                data-testid="stamp-count" required type="number" min="1" value={form.required_stamps}
                onChange={(e) => setForm({ ...form, required_stamps: e.target.value })}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white text-center text-xl font-bold focus:border-[#9D00FF]"
              />
            </Field>
          </div>
          <Field label="Reward Title">
            <input
              data-testid="stamp-reward-title" required value={form.reward_title}
              onChange={(e) => setForm({ ...form, reward_title: e.target.value })}
              placeholder="e.g. 1 free cocktail"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
          </Field>
          <Field label="Reward Description (optional)">
            <textarea
              value={form.reward_description}
              onChange={(e) => setForm({ ...form, reward_description: e.target.value })}
              rows={2}
              placeholder="Any cocktail from the menu. Not valid with other offers."
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF]"
            />
          </Field>
          <Field label="Reward expires after (days, optional)">
            <input
              type="number" min="0" value={form.reward_expires_days}
              onChange={(e) => setForm({ ...form, reward_expires_days: e.target.value })}
              placeholder="Leave empty for no expiry"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" data-testid="stamp-active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 accent-[#9D00FF]"
            />
            Active (staff can add stamps)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 min-h-[44px] font-semibold">Cancel</button>
            <button type="submit" disabled={loading} data-testid="stamp-submit" className="flex-1 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg py-2.5 min-h-[44px] font-semibold">
              {loading ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TriggerOffers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const { data } = await api.get("/offers");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (o) => {
    await api.put(`/offers/${o.id}`, { active: !o.active });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/offers/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setEditing({})}
          data-testid="new-offer"
          className="tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2"
        >
          <Plus size={16} /> New Offer
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && (
          <div className="col-span-full bg-[#1A1A1A] border border-dashed border-white/10 rounded-xl p-10 text-center">
            <Gift size={28} className="mx-auto text-[#9D00FF] mb-2" />
            <div className="font-semibold">No trigger offers yet</div>
            <p className="text-sm text-[#A0A0A0]">
              e.g. "Open a bottle tab → 1 free cocktail + 20% off food"
            </p>
          </div>
        )}
        {rows.map((o) => (
          <div key={o.id} data-testid={`offer-${o.id}`} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 flex flex-col">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#9D00FF] font-bold">OFFER</div>
                <h3 className="text-lg font-bold">{o.name}</h3>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-md border uppercase tracking-wider font-bold ${
                o.active
                  ? "bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40"
                  : "bg-white/10 text-[#A0A0A0] border-white/20"
              }`}>
                {o.active ? "Active" : "Paused"}
              </span>
            </div>
            <div className="text-xs text-[#A0A0A0]">{TRIGGER_LABEL[o.trigger_type]}</div>
            <p className="text-sm text-white mt-2">→ {o.reward_title}</p>
            {o.reward_description && (
              <p className="text-xs text-[#A0A0A0] mt-1">{o.reward_description}</p>
            )}
            <div className="text-[11px] text-[#A0A0A0] mt-2">
              {o.repeat ? "Repeats every trigger" : "Once per customer"}
              {o.reward_expires_days ? ` · Expires in ${o.reward_expires_days}d` : ""}
            </div>
            <div className="flex gap-2 mt-auto pt-3">
              <button onClick={() => setEditing(o)} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2 text-sm font-semibold hover:border-[#9D00FF]/50">Edit</button>
              <button onClick={() => toggle(o)} data-testid={`toggle-offer-${o.id}`} className="px-3 bg-[#0A0A0A] border border-white/10 rounded-lg text-sm hover:border-[#9D00FF]/50"><Power size={14} /></button>
              <button onClick={() => del(o.id)} data-testid={`delete-offer-${o.id}`} className="px-3 bg-[#0A0A0A] border border-[#EF4444]/30 rounded-lg text-sm text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <OfferForm
          offer={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function OfferForm({ offer, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: offer?.name || "",
    description: offer?.description || "",
    trigger_type: offer?.trigger_type || "on_tab_open",
    reward_title: offer?.reward_title || "",
    reward_description: offer?.reward_description || "",
    reward_expires_days: offer?.reward_expires_days || "",
    repeat: offer?.repeat || false,
    active: offer?.active !== false,
  });
  const [loading, setLoading] = useState(false);
  const editing = !!offer?.id;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        reward_expires_days: form.reward_expires_days ? Number(form.reward_expires_days) : null,
      };
      if (editing) {
        await api.put(`/offers/${offer.id}`, payload);
      } else {
        await api.post("/offers", payload);
      }
      toast.success(editing ? "Offer updated" : "Offer created");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl w-full max-w-lg p-6 tb-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{editing ? "Edit" : "New"} Offer</h2>
          <button onClick={onClose} className="p-2 text-[#A0A0A0] hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Offer name">
            <input required data-testid="offer-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Bottle Unlock Bonus"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]" />
          </Field>
          <Field label="Internal description">
            <textarea rows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Notes for staff"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF]" />
          </Field>
          <Field label="Trigger">
            <select
              data-testid="offer-trigger" value={form.trigger_type}
              onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]"
            >
              <option value="on_tab_open">When a bottle tab is opened</option>
              <option value="on_first_visit">On customer's first visit</option>
              <option value="manual">Manual grant only</option>
            </select>
          </Field>
          <Field label="Reward title">
            <input required data-testid="offer-reward-title" value={form.reward_title}
              onChange={(e) => setForm({ ...form, reward_title: e.target.value })}
              placeholder="e.g. 1 free cocktail + 20% off food"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]" />
          </Field>
          <Field label="Reward description (optional)">
            <textarea rows={2} value={form.reward_description}
              onChange={(e) => setForm({ ...form, reward_description: e.target.value })}
              placeholder="Terms, restrictions, etc."
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:border-[#9D00FF]" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Expires after (days)">
              <input type="number" min="0" value={form.reward_expires_days}
                onChange={(e) => setForm({ ...form, reward_expires_days: e.target.value })}
                placeholder="No expiry"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 min-h-[44px] text-white focus:border-[#9D00FF]" />
            </Field>
            <label className="flex items-center gap-2 text-sm self-end pb-2">
              <input type="checkbox" checked={form.repeat}
                onChange={(e) => setForm({ ...form, repeat: e.target.checked })}
                className="w-4 h-4 accent-[#9D00FF]" />
              Repeat every trigger
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-4 h-4 accent-[#9D00FF]" />
            Active
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 min-h-[44px] font-semibold">Cancel</button>
            <button type="submit" disabled={loading} data-testid="offer-submit" className="flex-1 tb-glow bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg py-2.5 min-h-[44px] font-semibold">
              {loading ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#A0A0A0] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
